/**
 * Dev-only shortcut that fills in the rest of onboarding so a developer can get to the app in one
 * click instead of ~30.
 *
 * <p>WHY IT GOES THROUGH THE REAL ENDPOINTS. It would be faster to POST a finished company straight
 * into storage, and it would also be worthless: the point of a seeded company is to exercise the same
 * code a real user does. So this drives the ordinary profile → create-company → answer-question loop,
 * one answer at a time, exactly as the wizard does. Progressive disclosure therefore works normally
 * (each answer returns the next question, so a gate opening reveals its children), and the backend's
 * SanitizeAnswers and parent gating apply untouched. Nothing here can put a state into storage that a
 * human clicking through could not.</p>
 *
 * <p>These are SIMULATED USER ANSWERS, not assumptions. That distinction is the whole of
 * COMPLIANCE-MODEL.md &#167;6: the model may never invent an answer to an unanswered predicate, and
 * this does not — it plays the part of a user who answers every question, and the answers land through
 * the normal path as that user's own. It is also why this is barred from anything but localhost: a
 * seeded company is fine as a fixture and would be a fabricated compliance record anywhere else.</p>
 */

import { createCompany, type CompanyType, type CompanyRegion } from '../lib/entities/companies';
import {
  answerQuestion,
  getNextQuestion,
  updateUserProfile,
  type OnboardingQuestion,
} from '../lib/entities/onboarding';

/**
 * Whether the shortcut may run at all. Both halves matter: `DEV` keeps it out of the production
 * bundle entirely, and the hostname check means even a dev build served to a colleague can't seed a
 * company. Guarded for SSR, where there is no `location` during prerender.
 */
export const canDevSeed = (): boolean =>
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

/** The demo business. A plain England-and-Wales Ltd with enough staff to trip the headcount rules. */
const DEMO = {
  firstName: 'Sam',
  lastName: 'Okonkwo',
  role: 'Founder',
  name: 'Demo Trading Ltd',
  companyType: 'limitedCompany' as CompanyType,
  region: 'england-wales' as CompanyRegion,
  declaredHeadcount: 12,
  description:
    'We run a small independent shop and café, with a mix of full-time, part-time and weekend staff.',
};

/**
 * The answer to give a question we have no specific value for.
 *
 * Booleans default to NO because that is genuinely the common answer — the specialist-rules screen
 * says so in its own copy — and because it is the conservative direction: "no" closes gates, so the
 * seeded company gets the baseline set rather than a checklist inflated by yeses nobody meant.
 */
function defaultAnswer(q: OnboardingQuestion): string {
  switch (q.kind) {
    case 'boolean':
      return 'false';
    case 'number':
      return '0';
    case 'choice':
      return q.options?.[0]?.value ?? '';
    case 'longText':
    case 'text':
    default:
      return DEMO.description;
  }
}

/** How many answers to give before assuming the loop will never end. */
const MAX_ANSWERS = 400;

export interface DevSeedResult {
  companyId: string;
  answered: number;
}

/**
 * Complete onboarding from wherever it currently stands.
 *
 * `companyId` is passed when the wizard has already created the company, so the shortcut can be used
 * from a later screen without creating a second one; omit it and the company is created here.
 */
export async function devSeedOnboarding(companyId?: string): Promise<DevSeedResult> {
  if (!canDevSeed()) throw new Error('The demo shortcut only runs on localhost in a dev build.');

  await updateUserProfile({
    firstName: DEMO.firstName,
    lastName: DEMO.lastName,
    role: DEMO.role,
  });

  const id =
    companyId ??
    (
      await createCompany({
        name: DEMO.name,
        companyType: DEMO.companyType,
        region: DEMO.region,
        declaredHeadcount: DEMO.declaredHeadcount,
      })
    ).id;

  // One question at a time, because each answer is what reveals the next. A group-at-a-time submit
  // would have to re-fetch after every screen anyway, and would skip questions that only appear once
  // an earlier answer on the SAME screen lands.
  let answered = 0;
  let next = await getNextQuestion(id);
  while (!next.done && next.question && answered < MAX_ANSWERS) {
    next = await answerQuestion(id, next.question.key, defaultAnswer(next.question));
    answered += 1;
  }

  if (!next.done) {
    // Not fatal: the caller still lands in the app, just with questions outstanding, which the
    // entity gates will re-ask. Worth saying out loud though, because it means either the question
    // set has grown past the cap or something is handing back the same question forever.
    console.warn(
      `[devSeed] stopped after ${answered} answers with questions still outstanding. ` +
        'Either the catalog has grown past MAX_ANSWERS or a question is not being recorded.',
    );
  }

  return { companyId: id, answered };
}
